import type { ApolloError } from '@apollo/client';
import { faRefresh } from '@fortawesome/pro-regular-svg-icons';
import {
  BarChart,
  BarChartDatum,
  Button,
  Heading,
  Icon,
  Spinner,
  Surface,
  Text,
  getIcon,
} from '@quality24/design-system';
import classNames from 'classnames';
import * as React from 'react';

import type { DatumValue } from '@nivo/core';
import { merge } from 'lodash';
import {
  BarChartComponentData,
  DateFilter,
  ValueFormatter,
} from '../../../../@types';

import {
  formatValue,
  parseDataValue,
  sanitizeKey,
} from '../../../../utils/dataUtils';
import { filterData } from '../../utils/filter';
import { mapData } from '../../utils/mapper';
import { createReferenceMap } from '../../utils/reference';
import { calculatePercentages, getChartKeys } from './utils';

import styles from '../../DataViewContainer.module.scss';

export interface Props extends React.HTMLAttributes<HTMLElement> {
  loading?: boolean;
  error?: ApolloError;
  refetch: () => void;
  data: Record<string, Array<Record<string, unknown>>>;
  dateFilter: DateFilter;
  renderOnSurface?: boolean;
  config: Omit<BarChartComponentData, 'type'>;
}

/**
 * <BarChartContainer> page
 */
export const BarChartContainer: React.FunctionComponent<Props> = ({
  className,
  loading,
  error,
  refetch,
  data,
  dateFilter,
  renderOnSurface = true,
  config: {
    title,
    className: configClassName,
    options,
    height,
    allowScroll,
    displayLegend,
    allowDecimalScale,
    summaryBadge,
    postQuery,
    rootKey,
    aggrKey,
    references,
    groupBy,
    surfaceClassName,
  },
  ...htmlElementProps
}) => {
  /**
   * Gets the aggregation data for this container
   */
  const aggrData = React.useMemo(
    () => filterData(data, rootKey, postQuery),
    [data, rootKey, postQuery],
  );

  /**
   * Creates the reference map
   */
  const referenceMap = React.useMemo(
    () => createReferenceMap({ data, references, dateFilter, postQuery }),
    [data, references, dateFilter, postQuery],
  );

  /**
   * Maps data
   */
  const mappedData = React.useMemo(
    () =>
      mapData({
        data: aggrData,
        groupBy,
        aggrKey,
        reference: referenceMap,
      }) as BarChartDatum[],
    [aggrData, groupBy, aggrKey, referenceMap],
  );

  /** Map data for summary badge */
  const summaryData = React.useMemo(
    () =>
      summaryBadge
        ? (mapData({
            // A badge can aggregate a different node than the chart via its own
            // `rootKey`; otherwise it uses the chart's aggregated data.
            data: summaryBadge.rootKey
              ? filterData(data, summaryBadge.rootKey)
              : aggrData,
            groupBy: summaryBadge.groupBy,
            aggrKey: summaryBadge.aggrKey,
            reference: referenceMap,
          }) as BarChartDatum[])
        : undefined,
    [aggrData, summaryBadge, referenceMap, data],
  );

  // Get the chart keys
  const keys = React.useMemo(
    () => getChartKeys(groupBy, referenceMap),
    [groupBy, referenceMap],
  );

  /**
   * Post-processing - Calculate percentages
   */
  const chartData = React.useMemo(
    () => calculatePercentages(aggrKey, mappedData, keys),
    [aggrKey, mappedData, keys],
  );

  // Add log for debuging with DVC-Designer
  if (process.env.REACT_APP_HOST_PROJECT === 'designer') {
    // eslint-disable-next-line no-console
    console.info('BarChartContainer', {
      originalData: data,
      aggregatedData: aggrData,
      referenceMap,
      mappedData,
      chartData,
      indexBy: sanitizeKey(aggrKey.key),
      groupKeys: keys,
    });
  }

  const processedOptions = React.useMemo(() => {
    const getFormatterFunction = (formatter: ValueFormatter) => {
      return (value: DatumValue) => {
        if (['string', 'number'].includes(typeof value)) {
          const result = formatValue(value as string | number, formatter);

          if (typeof result === 'number') {
            return result.toString();
          }

          return result;
        }

        if (value instanceof Date) {
          return value.toLocaleDateString();
        }

        return value.toString();
      };
    };
    const newOptions = { ...options };

    if (options?.customFormatter) {
      newOptions.valueFormat = getFormatterFunction(options.customFormatter);
    }

    if (options?.axisLeftCustomFormatter) {
      newOptions.axisLeft = merge(newOptions.axisLeft, {
        format: getFormatterFunction(options.axisLeftCustomFormatter),
      });
    }

    if (options?.axisBottomCustomFormatter) {
      newOptions.axisBottom = merge(newOptions.axisBottom, {
        format: getFormatterFunction(options.axisBottomCustomFormatter),
      });
    }

    if (options?.axisRightCustomFormatter) {
      newOptions.axisRight = merge(newOptions.axisRight, {
        format: getFormatterFunction(options.axisRightCustomFormatter),
      });
    }

    if (options?.axisTopCustomFormatter) {
      newOptions.axisTop = merge(newOptions.axisTop, {
        format: getFormatterFunction(options.axisTopCustomFormatter),
      });
    }

    return newOptions;
  }, [options]);

  return (
    <div
      className={classNames(className, configClassName)}
      {...htmlElementProps}
    >
      {summaryBadge && summaryData && (
        <div className="d-flex gap-2 align-items-center m-2">
          {!!summaryBadge.icon && (
            <div
              className="rounded-md bg-blue600_25 d-flex align-items-center justify-content-center"
              style={{ height: 46, width: 46 }}
            >
              <Icon icon={getIcon(summaryBadge.icon)} color="primary" />
            </div>
          )}
          <div className="mb-2">
            <Text size="sm2">{`${summaryBadge?.label}:`}</Text>
            <Text weight="bold" size="lg2">
              {formatValue(
                parseDataValue(
                  summaryData[0],
                  summaryBadge?.value,
                  summaryBadge.dataType || 'string',
                ) as string,
                summaryBadge.formatter,
                summaryBadge.mapping,
              ) || '-'}
            </Text>
          </div>
        </div>
      )}

      <Surface
        className={classNames(styles.surface, surfaceClassName, {
          'on-surface': renderOnSurface,
        })}
        padding="sm"
        backgroundColor="grayLight2"
      >
        {title && (
          <Heading as="h2" size="sm2" weight="bold" className="mb-4">
            {!loading && parseDataValue(data, title, 'string')}
          </Heading>
        )}

        {!loading && !error && aggrData.length === 0 && (
          <Text size="sm2" color="gray100">
            Não há dados disponíveis.
          </Text>
        )}

        {!loading && error && (
          <div className={styles['error-container']}>
            <Text className="mb-2" size="sm2" color="gray100">
              Não foi possível carregar os dados.
            </Text>
            <Button
              variant="outline"
              icon={faRefresh}
              onClick={() => refetch()}
            >
              Recarregar
            </Button>
          </div>
        )}

        {loading && <Spinner className="m-4 d-flex flex-justify-center" />}

        {!error && !loading && aggrData.length > 0 && (
          <BarChart
            data={chartData}
            options={processedOptions}
            indexBy={sanitizeKey(aggrKey.key)}
            keys={keys}
            height={height}
            tooltipShowPercentage={
              options?.tooltipShowPercentage !== false
                ? keys.length >= 1
                : false
            }
            displayLegend={displayLegend !== false}
            allowDecimalScale={allowDecimalScale !== false}
            allowScroll={allowScroll}
          />
        )}
      </Surface>
    </div>
  );
};

export default BarChartContainer;
